import './ServerTextTranslationMessage.scss'

import { useAtom } from 'jotai'
import React, { useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkBreaks from 'remark-breaks'

import { AccordionItem, AccordionItemContent, AccordionItemTrigger, AccordionRoot } from '@/components/ui/accordion'
import { Container } from '@/components/ui/container'
import { Flex } from '@/components/ui/flex'
import { Text } from '@/components/ui/typography'
import { getFullLangueName, translateTextEventsAtom } from '@/store/translate'
import { IShortMessageObject } from '@/types/gigiChat/Chat'
import { IConversationSettings } from '@/types/gigiChat/ConversationSettings'

import ErrorMessage from '../serverMessage/ErrorMessage'
import TypingDots from '../typingDots/TypingDots'
import AnswerTextTranslationMarkdown from './components/answerTextTranslationMarkdown'

const ServerTextTranslationMessage = React.memo(
    ({
        item,
        isLastItem,
        question,
        answer,
    }: {
        item: IShortMessageObject
        isLastItem: boolean
        question: string
        answer: string | null
    }) => {
        const questionMaxLength = 240
        const [translateEvents] = useAtom(translateTextEventsAtom)
        const [isOpen, setIsOpen] = useState<boolean>(false)
        return (
            <>
                <Container my="1" maxWidth="unset" variant="user">
                    {question.length < questionMaxLength && (
                        <>
                            <Text fontSize="14px">Translate to: {getFullLangueName(item.settings as IConversationSettings)}</Text>
                            <Text fontSize="14px" textAlign="left" pt="8px">
                                {question}
                            </Text>
                        </>
                    )}
                    {question.length > questionMaxLength && (
                        <AccordionRoot>
                            <AccordionItem border="none" value="">
                                <AccordionItemTrigger
                                    p="0"
                                    justifyContent="space-between"
                                    alignItems="flex-start"
                                    _hover={{ backgroundColor: 'transparent' }}
                                    onClick={() => setIsOpen(!isOpen)}
                                >
                                    <Flex flexDir="column">
                                        <Text fontSize="14px" textAlign="left">
                                            Translate to: {getFullLangueName(item.settings as IConversationSettings)}
                                        </Text>
                                        {!isOpen && (
                                            <Text fontSize="14px" textAlign="left" pt="8px">
                                                {`${question.slice(0, questionMaxLength)}...`}
                                            </Text>
                                        )}
                                    </Flex>
                                </AccordionItemTrigger>
                                <AccordionItemContent p="0" pt="12px" fontSize="14px" letterSpacing="0.25px">
                                    <Text
                                        className="ServerTextTranslationMessage"
                                        lineHeight="20px"
                                        fontSize="14px"
                                        as="div"
                                        overflowX="auto"
                                    >
                                        <ReactMarkdown remarkPlugins={[remarkBreaks]} children={question || ''} />
                                    </Text>
                                </AccordionItemContent>
                            </AccordionItem>
                        </AccordionRoot>
                    )}
                </Container>
                {item.structuredAnswer && item.structuredAnswer?.textTranslate && !item.instructionalErrorMessageId && (
                    <Container
                        variant="server"
                        mt="1"
                        maxWidth="unset"
                        borderRadius={item.citations && item.citations.length > 0 ? '' : '4px'}
                        borderTopRadius={item.citations && item.citations.length > 0 ? '4px' : ''}
                        id={item.userPromptId?.toString()}
                        display={item.isStreaming ? 'none' : 'block'}
                    >
                        <AnswerTextTranslationMarkdown
                            data={
                                Array.isArray(item.structuredAnswer.textTranslate) ? item.structuredAnswer.textTranslate : translateEvents
                            }
                            languagesCodes={item.settings?.processingStrategyTranslation.languages || []}
                        />
                    </Container>
                )}

                {answer && !item.isStreaming && item.instructionalErrorMessageId && <ErrorMessage message={answer} />}
                {!answer && !item.isStreaming && <ErrorMessage />}
                {item.isStreaming && isLastItem && <TypingDots />}
            </>
        )
    },
)

export default ServerTextTranslationMessage
